import React, { useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import HeaderMenu from '../Header/HeaderMenu';

import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ApartmentIcon from '@mui/icons-material/Apartment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import CalendarViewYearIcon from '@mui/icons-material/CalendarViewMonth';
import StorefrontIcon from '@mui/icons-material/Storefront';

const money = (value) =>
  `₹ ${Number(value || 0).toLocaleString('en-IN', {
    maximumFractionDigits: 2,
  })}`;

const DEMO_COMPANIES = [
  {
    id: 1,
    name: 'ABC Group',
    properties: [
      {
        id: 101,
        name: 'Green Villa',
        type: 'Hostel',
        openingBalance: 50000, // Opening balance as at 1 Jan 2026
      },
      {
        id: 102,
        name: 'Sunrise Residency',
        type: 'Hotel',
        openingBalance: 30000,
      },
      {
        id: 103,
        name: 'Coastal Retreat',
        type: 'Resort',
        openingBalance: 80000,
      },
      {
        id: 104,
        name: 'City Suites',
        type: 'Service Apartment',
        openingBalance: 40000,
      },
    ],
  },
  {
    id: 2,
    name: 'XYZ Group',
    properties: [
      {
        id: 201,
        name: 'Royal Stay',
        type: 'Hotel',
        openingBalance: 65000,
      },
      {
        id: 202,
        name: 'Palm Grove',
        type: 'Resort',
        openingBalance: 90000,
      },
    ],
  },
];

// Demo transactions only. Replace this array with your API response.
// type: 'IN' means money received; type: 'OUT' means money paid.
const DEMO_TRANSACTIONS = [
  { id: 1, companyId: 1, propertyId: 101, date: '2026-01-05', type: 'IN', category: 'Room Rent', description: 'Monthly rent collection', amount: 120000 },
  { id: 2, companyId: 1, propertyId: 101, date: '2026-01-10', type: 'OUT', category: 'Purchase', description: 'Grocery and supplies', amount: 35000 },
  { id: 3, companyId: 1, propertyId: 101, date: '2026-02-05', type: 'IN', category: 'Room Rent', description: 'Monthly rent collection', amount: 125000 },
  { id: 4, companyId: 1, propertyId: 101, date: '2026-02-12', type: 'OUT', category: 'Staff', description: 'Staff salary', amount: 42000 },
  { id: 5, companyId: 1, propertyId: 101, date: '2026-03-05', type: 'IN', category: 'Room Rent', description: 'Monthly rent collection', amount: 130000 },
  { id: 6, companyId: 1, propertyId: 101, date: '2026-03-14', type: 'OUT', category: 'Maintenance', description: 'Plumbing work', amount: 18000 },
  { id: 7, companyId: 1, propertyId: 101, date: '2026-11-03', type: 'IN', category: 'Room Rent', description: 'November collection', amount: 135000 },
  { id: 8, companyId: 1, propertyId: 101, date: '2026-11-08', type: 'OUT', category: 'Purchase', description: 'Monthly supplies', amount: 38000 },

  { id: 9, companyId: 1, propertyId: 102, date: '2026-01-08', type: 'IN', category: 'Room Booking', description: 'Guest payments', amount: 90000 },
  { id: 10, companyId: 1, propertyId: 102, date: '2026-01-15', type: 'OUT', category: 'Staff', description: 'Staff salary', amount: 30000 },
  { id: 11, companyId: 1, propertyId: 102, date: '2026-02-08', type: 'IN', category: 'Room Booking', description: 'Guest payments', amount: 105000 },
  { id: 12, companyId: 1, propertyId: 102, date: '2026-02-16', type: 'OUT', category: 'Utilities', description: 'Electricity and water', amount: 16000 },
  { id: 13, companyId: 1, propertyId: 102, date: '2026-11-06', type: 'IN', category: 'Room Booking', description: 'Guest payments', amount: 110000 },
  { id: 14, companyId: 1, propertyId: 102, date: '2026-11-17', type: 'OUT', category: 'Maintenance', description: 'AC servicing', amount: 12000 },

  { id: 15, companyId: 1, propertyId: 103, date: '2026-01-12', type: 'IN', category: 'Bookings', description: 'Guest bookings', amount: 160000 },
  { id: 16, companyId: 1, propertyId: 103, date: '2026-01-18', type: 'OUT', category: 'Operations', description: 'Resort operations', amount: 70000 },
  { id: 17, companyId: 1, propertyId: 103, date: '2026-02-12', type: 'IN', category: 'Bookings', description: 'Guest bookings', amount: 175000 },
  { id: 18, companyId: 1, propertyId: 103, date: '2026-02-20', type: 'OUT', category: 'Staff', description: 'Staff salary', amount: 65000 },
  { id: 19, companyId: 1, propertyId: 103, date: '2026-11-11', type: 'IN', category: 'Bookings', description: 'November bookings', amount: 190000 },
  { id: 20, companyId: 1, propertyId: 103, date: '2026-11-21', type: 'OUT', category: 'Purchase', description: 'Food and supplies', amount: 72000 },

  { id: 21, companyId: 1, propertyId: 104, date: '2026-01-06', type: 'IN', category: 'Rent', description: 'Apartment rent', amount: 75000 },
  { id: 22, companyId: 1, propertyId: 104, date: '2026-01-20', type: 'OUT', category: 'Maintenance', description: 'Building maintenance', amount: 22000 },
  { id: 23, companyId: 1, propertyId: 104, date: '2026-11-09', type: 'IN', category: 'Rent', description: 'Apartment rent', amount: 80000 },
  { id: 24, companyId: 1, propertyId: 104, date: '2026-11-19', type: 'OUT', category: 'Utilities', description: 'Utility bills', amount: 19000 },

  { id: 25, companyId: 2, propertyId: 201, date: '2026-01-04', type: 'IN', category: 'Room Booking', description: 'Guest payments', amount: 140000 },
  { id: 26, companyId: 2, propertyId: 201, date: '2026-01-14', type: 'OUT', category: 'Staff', description: 'Staff salary', amount: 55000 },
  { id: 27, companyId: 2, propertyId: 201, date: '2026-11-07', type: 'IN', category: 'Room Booking', description: 'Guest payments', amount: 155000 },
  { id: 28, companyId: 2, propertyId: 201, date: '2026-11-16', type: 'OUT', category: 'Purchase', description: 'Hotel supplies', amount: 48000 },

  { id: 29, companyId: 2, propertyId: 202, date: '2026-01-09', type: 'IN', category: 'Bookings', description: 'Guest bookings', amount: 180000 },
  { id: 30, companyId: 2, propertyId: 202, date: '2026-01-22', type: 'OUT', category: 'Operations', description: 'Resort operations', amount: 85000 },
  { id: 31, companyId: 2, propertyId: 202, date: '2026-11-12', type: 'IN', category: 'Bookings', description: 'Guest bookings', amount: 205000 },
  { id: 32, companyId: 2, propertyId: 202, date: '2026-11-24', type: 'OUT', category: 'Maintenance', description: 'Pool maintenance', amount: 52000 },
];

const CompanyCashFlow = () => {
  const [companyId, setCompanyId] = useState(1);
  const [periodType, setPeriodType] = useState('MONTHLY');
  const [selectedMonth, setSelectedMonth] = useState('2026-11');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [propertyId, setPropertyId] = useState('ALL');
  const [appliedFilters, setAppliedFilters] = useState({
    companyId: 1,
    periodType: 'MONTHLY',
    month: '2026-11',
    year: '2026',
    propertyId: 'ALL',
  });
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const selectedCompany = DEMO_COMPANIES.find(
    (company) => company.id === appliedFilters.companyId
  );

  const filteredProperties = useMemo(() => {
    if (!selectedCompany) return [];
    if (appliedFilters.propertyId === 'ALL') {
      return selectedCompany.properties;
    }
    return selectedCompany.properties.filter(
      (property) => property.id === Number(appliedFilters.propertyId)
    );
  }, [selectedCompany, appliedFilters.propertyId]);

  const companyTransactions = useMemo(() => {
    return DEMO_TRANSACTIONS.filter(
      (transaction) => transaction.companyId === appliedFilters.companyId
    );
  }, [appliedFilters.companyId]);

  const isInPeriod = (date, periodType, month, year) => {
    if (periodType === 'YEARLY') {
      return date.startsWith(`${year}-`);
    }
    return date.startsWith(`${month}`);
  };

  const isBeforePeriod = (date, periodType, month, year) => {
    if (periodType === 'YEARLY') {
      return date < `${year}-01-01`;
    }
    return date < `${month}-01`;
  };

  const propertyRows = useMemo(() => {
    return filteredProperties.map((property) => {
      const propertyTransactions = companyTransactions.filter(
        (transaction) => transaction.propertyId === property.id
      );

      const openingMovement = propertyTransactions
        .filter((transaction) =>
          isBeforePeriod(
            transaction.date,
            appliedFilters.periodType,
            appliedFilters.month,
            appliedFilters.year
          )
        )
        .reduce(
          (total, transaction) =>
            total + (transaction.type === 'IN' ? transaction.amount : -transaction.amount),
          0
        );

      const periodTransactions = propertyTransactions.filter((transaction) =>
        isInPeriod(
          transaction.date,
          appliedFilters.periodType,
          appliedFilters.month,
          appliedFilters.year
        )
      );

      const cashIn = periodTransactions
        .filter((transaction) => transaction.type === 'IN')
        .reduce((total, transaction) => total + transaction.amount, 0);

      const cashOut = periodTransactions
        .filter((transaction) => transaction.type === 'OUT')
        .reduce((total, transaction) => total + transaction.amount, 0);

      const openingBalance = property.openingBalance + openingMovement;

      return {
        ...property,
        openingBalance,
        cashIn,
        cashOut,
        netCashFlow: cashIn - cashOut,
        closingBalance: openingBalance + cashIn - cashOut,
        transactions: periodTransactions,
      };
    });
  }, [filteredProperties, companyTransactions, appliedFilters]);

  const totals = useMemo(() => {
    return propertyRows.reduce(
      (result, property) => ({
        openingBalance: result.openingBalance + property.openingBalance,
        cashIn: result.cashIn + property.cashIn,
        cashOut: result.cashOut + property.cashOut,
        netCashFlow: result.netCashFlow + property.netCashFlow,
        closingBalance: result.closingBalance + property.closingBalance,
      }),
      {
        openingBalance: 0,
        cashIn: 0,
        cashOut: 0,
        netCashFlow: 0,
        closingBalance: 0,
      }
    );
  }, [propertyRows]);



  const formatPeriod = () => {
    if (appliedFilters.periodType === 'YEARLY') {
      return `Year ${appliedFilters.year}`;
    }
    const [year, month] = appliedFilters.month.split('-');
    return new Date(Number(year), Number(month) - 1).toLocaleString('en-IN', {
      month: 'long',
      year: 'numeric',
    });
  };

  const applyFilters = () => {
    setAppliedFilters({
      companyId,
      periodType,
      month: selectedMonth,
      year: selectedYear,
      propertyId,
    });
  };

  const openPropertyDetails = (property) => {
    setSelectedProperty(property);
    setDetailsOpen(true);
  };

  const exportCsv = () => {
    const headers = [
      'Property Name',
      'Property Type',
      'Opening Balance',
      'Cash In',
      'Cash Out',
      'Net Cash Flow',
      'Closing Balance',
    ];

    const rows = propertyRows.map((property) => [
      property.name,
      property.type,
      property.openingBalance,
      property.cashIn,
      property.cashOut,
      property.netCashFlow,
      property.closingBalance,
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`)
          .join(',')
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `company-cash-flow-${appliedFilters.periodType.toLowerCase()}-${appliedFilters.periodType === 'YEARLY' ? appliedFilters.year : appliedFilters.month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const MetricCard = ({ title, value, icon, color, subtitle }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        height: '100%',
        borderRadius: 3,
        border: '1px solid #e7edf4',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography variant="h5" fontWeight={750} sx={{ mt: 1, color }}>
            {money(value)}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            bgcolor: `${color}14`,
            color,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          {icon}
        </Box>
      </Stack>
    </Paper>
  );

  return (
    <>
      <HeaderMenu />

      <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', py: 4 }}>
        <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 3 } }}>
          {/* Page heading */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 3 },
              mb: 3,
              borderRadius: 3,
              color: '#fff',
              background: 'linear-gradient(135deg, #154c8a 0%, #2584d8 100%)',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <AccountBalanceWalletIcon sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h5" fontWeight={750}>
                  Company Cash Flow
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Consolidated income and expense overview across all properties
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* Filters */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={2}
              mb={2.5}
            >
              <Box>
                <Typography fontWeight={750}>Cash Flow Filters</Typography>
                <Typography variant="body2" color="text.secondary">
                  Select a company, reporting period and property
                </Typography>
              </Box>

              <ToggleButtonGroup
                exclusive
                size="small"
                value={periodType}
                onChange={(event, value) => {
                  if (value) setPeriodType(value);
                }}
                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
              >
                <ToggleButton value="MONTHLY">
                  <CalendarMonthIcon sx={{ mr: 1, fontSize: 18 }} />
                  Monthly
                </ToggleButton>
                <ToggleButton value="YEARLY">
                  <CalendarViewYearIcon sx={{ mr: 1, fontSize: 18 }} />
                  Yearly
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={companyId}
                    label="Company"
                    onChange={(event) => {
                      setCompanyId(event.target.value);
                      setPropertyId('ALL');
                    }}
                  >
                    {DEMO_COMPANIES.map((company) => (
                      <MenuItem key={company.id} value={company.id}>
                        {company.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {periodType === 'MONTHLY' ? (
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    type="month"
                    label="Month"
                    InputLabelProps={{ shrink: true }}
                    value={selectedMonth}
                    onChange={(event) => setSelectedMonth(event.target.value)}
                  />
                </Grid>
              ) : (
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Year</InputLabel>
                    <Select
                      value={selectedYear}
                      label="Year"
                      onChange={(event) => setSelectedYear(event.target.value)}
                    >
                      {['2024', '2025', '2026', '2027'].map((year) => (
                        <MenuItem key={year} value={year}>
                          {year}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Property</InputLabel>
                  <Select
                    value={propertyId}
                    label="Property"
                    onChange={(event) => setPropertyId(event.target.value)}
                  >
                    <MenuItem value="ALL">All Properties</MenuItem>
                    {(DEMO_COMPANIES.find((item) => item.id === companyId)?.properties || []).map(
                      (property) => (
                        <MenuItem key={property.id} value={String(property.id)}>
                          {property.name} ({property.type})
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={applyFilters}
                  sx={{ height: 40, borderRadius: 2, textTransform: 'none', fontWeight: 700 }}
                >
                  Apply Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Selected company and period */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1}
            mb={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={750}>
                {selectedCompany?.name || 'Company'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatPeriod()} · {propertyId === 'ALL' ? 'All properties' : 'Selected property'}
              </Typography>
            </Box>
            <Chip
              icon={<ApartmentIcon />}
              label={`${propertyRows.length} ${propertyRows.length === 1 ? 'Property' : 'Properties'}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          {/* Summary cards */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Total Cash In"
                value={totals.cashIn}
                color="#21834a"
                icon={<TrendingUpIcon />}
                subtitle="Money received in selected period"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Total Cash Out"
                value={totals.cashOut}
                color="#d04444"
                icon={<TrendingDownIcon />}
                subtitle="Money paid in selected period"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Net Cash Flow"
                value={totals.netCashFlow}
                color={totals.netCashFlow >= 0 ? '#1769aa' : '#d04444'}
                icon={<AccountBalanceWalletIcon />}
                subtitle="Cash in minus cash out"
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard
                title="Closing Balance"
                value={totals.closingBalance}
                color="#8755b2"
                icon={<ApartmentIcon />}
                subtitle="Opening balance plus net cash flow"
              />
            </Grid>
          </Grid>

          {/* Opening / closing and property count */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Opening Balance
                </Typography>
                <Typography variant="h6" fontWeight={750}>
                  {money(totals.openingBalance)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Total Properties
                </Typography>
                <Typography variant="h6" fontWeight={750}>
                  {propertyRows.length}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hotels, hostels, resorts, apartments and other property types
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Reporting Period
                </Typography>
                <Typography variant="h6" fontWeight={750}>
                  {periodType === 'YEARLY' ? 'Yearly' : 'Monthly'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatPeriod()}
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Property-wise table */}
          <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              spacing={2}
              mb={2.5}
            >
              <Box>
                <Typography variant="h6" fontWeight={750}>
                  Property-wise Cash Flow
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Compare income, expenses and closing balances for each property
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon />}
                onClick={exportCsv}
                sx={{ textTransform: 'none', alignSelf: { xs: 'flex-start', sm: 'center' } }}
              >
                Export CSV
              </Button>
            </Stack>

            {propertyRows.length === 0 ? (
              <Alert severity="info">No properties found for this company.</Alert>
            ) : (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f4f7fb' }}>
                      <TableCell><b>Property</b></TableCell>
                      <TableCell><b>Type</b></TableCell>
                      <TableCell align="right"><b>Opening Balance</b></TableCell>
                      <TableCell align="right"><b>Cash In</b></TableCell>
                      <TableCell align="right"><b>Cash Out</b></TableCell>
                      <TableCell align="right"><b>Net Flow</b></TableCell>
                      <TableCell align="right"><b>Closing Balance</b></TableCell>
                      <TableCell align="center"><b>Action</b></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {propertyRows.map((property) => (
                      <TableRow key={property.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <StorefrontIcon color="action" fontSize="small" />
                            <Typography fontWeight={650}>{property.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Chip label={property.type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell align="right">{money(property.openingBalance)}</TableCell>
                        <TableCell align="right">
                          <Typography color="success.main" fontWeight={650}>
                            {money(property.cashIn)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography color="error.main" fontWeight={650}>
                            {money(property.cashOut)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight={700}
                            color={property.netCashFlow >= 0 ? 'success.main' : 'error.main'}
                          >
                            {money(property.netCashFlow)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={750}>{money(property.closingBalance)}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<VisibilityIcon />}
                            onClick={() => openPropertyDetails(property)}
                            sx={{ textTransform: 'none' }}
                          >
                            View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell colSpan={2}><b>Company Total</b></TableCell>
                      <TableCell align="right"><b>{money(totals.openingBalance)}</b></TableCell>
                      <TableCell align="right">
                        <Typography color="success.main" fontWeight={750}>{money(totals.cashIn)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography color="error.main" fontWeight={750}>{money(totals.cashOut)}</Typography>
                      </TableCell>
                      <TableCell align="right"><b>{money(totals.netCashFlow)}</b></TableCell>
                      <TableCell align="right"><b>{money(totals.closingBalance)}</b></TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            )}
          </Paper>

        </Box>
      </Box>

      {/* Property details dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <StorefrontIcon color="primary" />
            <Box>
              <Typography variant="h6" fontWeight={750}>
                {selectedProperty?.name || 'Property'} - Cash Flow
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedProperty?.type} · {formatPeriod()}
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          {selectedProperty && (
            <>
              <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Opening Balance</Typography>
                  <Typography fontWeight={750}>{money(selectedProperty.openingBalance)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Cash In</Typography>
                  <Typography fontWeight={750} color="success.main">{money(selectedProperty.cashIn)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Cash Out</Typography>
                  <Typography fontWeight={750} color="error.main">{money(selectedProperty.cashOut)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Closing Balance</Typography>
                  <Typography fontWeight={750}>{money(selectedProperty.closingBalance)}</Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography fontWeight={700} mb={1}>Transactions</Typography>
              {selectedProperty.transactions.length === 0 ? (
                <Alert severity="info">No transactions for this property in the selected period.</Alert>
              ) : (
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><b>Date</b></TableCell>
                        <TableCell><b>Category</b></TableCell>
                        <TableCell><b>Description</b></TableCell>
                        <TableCell><b>Type</b></TableCell>
                        <TableCell align="right"><b>Amount</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedProperty.transactions
                        .slice()
                        .sort((a, b) => b.date.localeCompare(a.date))
                        .map((transaction) => (
                          <TableRow key={transaction.id}>
                            <TableCell>{transaction.date}</TableCell>
                            <TableCell>{transaction.category}</TableCell>
                            <TableCell>{transaction.description}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={transaction.type === 'IN' ? 'Cash In' : 'Cash Out'}
                                color={transaction.type === 'IN' ? 'success' : 'error'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                fontWeight={700}
                                color={transaction.type === 'IN' ? 'success.main' : 'error.main'}
                              >
                                {money(transaction.amount)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CompanyCashFlow;
